import { Component, OnInit, ViewChild, TemplateRef } from '@angular/core';
import { NgbModal, NgbModalRef, NgbOffcanvas, NgbOffcanvasRef } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { ToastService } from '../../../../services/toast-service';
import {
  KaiPromptService,
  KAIPromptDto,
  KAIPromptVersionDto,
  UpdatePromptRequest,
  PromptWorkshopResponse
} from '../../../../services/kai-prompt.service';

@Component({
  selector: 'app-kai-prompts',
  templateUrl: './kai-prompts.component.html',
  styleUrls: ['./kai-prompts.component.scss']
})
export class KaiPromptsComponent implements OnInit {
  @ViewChild('editDrawer') editDrawer!: TemplateRef<any>;
  @ViewChild('historyModal') historyModal!: TemplateRef<any>;
  @ViewChild('testModal') testModal!: TemplateRef<any>;
  @ViewChild('workshopModal') workshopModal!: TemplateRef<any>;
  @ViewChild('resetConfirmModal') resetConfirmModal!: TemplateRef<any>;

  prompts: KAIPromptDto[] = [];
  filteredPrompts: KAIPromptDto[] = [];
  modules: string[] = [];
  selectedModule = '';
  searchTerm = '';
  loading = false;

  // Edit state
  selectedPrompt: KAIPromptDto | null = null;
  editValue = '';
  editConstraints = '';
  editChangeNote = '';
  saving = false;
  private offcanvasRef?: NgbOffcanvasRef;

  // History state
  versionHistory: KAIPromptVersionDto[] = [];
  historyLoading = false;
  private historyModalRef?: NgbModalRef;

  // Test state
  testMessage = '';
  testResult = '';
  testTokens = 0;
  testCost = 0;
  testing = false;
  private testModalRef?: NgbModalRef;

  // Workshop state
  workshopOperation = 'validate';
  workshopContext = '';
  workshopResult = '';
  workshopTokens = 0;
  workshopLoading = false;
  private workshopModalRef?: NgbModalRef;

  // Reset state
  promptToReset: KAIPromptDto | null = null;
  private resetModalRef?: NgbModalRef;

  constructor(
    private promptService: KaiPromptService,
    private modalService: NgbModal,
    private offcanvasService: NgbOffcanvas,
    private toastService: ToastService,
    private translateService: TranslateService
  ) {}

  ngOnInit(): void {
    this.loadPrompts();
  }

  loadPrompts(): void {
    this.loading = true;
    this.promptService.getPrompts().subscribe({
      next: (response) => {
        if (response?.success) {
          this.prompts = response.result || [];
          this.modules = [...new Set(this.prompts.map(p => p.module))].sort();
          this.applyFilters();
        }
        this.loading = false;
      },
      error: () => {
        this.loading = false;
      }
    });
  }

  applyFilters(): void {
    let result = [...this.prompts];
    if (this.selectedModule) {
      result = result.filter(p => p.module === this.selectedModule);
    }
    if (this.searchTerm.trim()) {
      const term = this.searchTerm.toLowerCase();
      result = result.filter(p =>
        p.promptKey.toLowerCase().includes(term) ||
        (p.description || '').toLowerCase().includes(term) ||
        p.promptValue.toLowerCase().includes(term)
      );
    }
    this.filteredPrompts = result;
  }

  onModuleChange(): void {
    this.applyFilters();
  }

  onSearchChange(): void {
    this.applyFilters();
  }

  // ═══════════════════════════════════════════
  // Edit Drawer
  // ═══════════════════════════════════════════

  openEdit(prompt: KAIPromptDto): void {
    this.selectedPrompt = prompt;
    this.editValue = prompt.promptValue;
    this.editConstraints = prompt.constraints || '';
    this.editChangeNote = '';
    this.offcanvasRef = this.offcanvasService.open(this.editDrawer, {
      position: 'end',
      panelClass: 'kai-prompt-drawer'
    });
  }

  savePrompt(): void {
    if (!this.selectedPrompt) return;
    this.saving = true;

    const req: UpdatePromptRequest = {
      promptKey: this.selectedPrompt.promptKey,
      promptValue: this.editValue,
      constraints: this.editConstraints || undefined,
      changeNote: this.editChangeNote || undefined
    };

    this.promptService.updatePrompt(req).subscribe({
      next: (response) => {
        if (response?.success) {
          this.toastService.show({
            type: 'success',
            data: [this.translateService.instant('KAIPrompts.savedSuccessfully')]
          });
          this.offcanvasRef?.close();
          this.loadPrompts();
        } else {
          this.toastService.show({
            type: 'error',
            data: [this.translateService.instant('KAIPrompts.saveError')]
          });
        }
        this.saving = false;
      },
      error: () => {
        this.saving = false;
        this.toastService.show({
          type: 'error',
          data: [this.translateService.instant('KAIPrompts.saveError')]
        });
      }
    });
  }

  cancelEdit(): void {
    this.offcanvasRef?.close();
    this.selectedPrompt = null;
  }

  // ═══════════════════════════════════════════
  // Version History
  // ═══════════════════════════════════════════

  openHistory(prompt: KAIPromptDto): void {
    this.selectedPrompt = prompt;
    this.versionHistory = [];
    this.historyLoading = true;
    this.historyModalRef = this.modalService.open(this.historyModal, {
      size: 'lg',
      centered: true,
      backdrop: 'static'
    });

    this.promptService.getHistory(prompt.promptKey).subscribe({
      next: (response) => {
        if (response?.success) {
          this.versionHistory = response.result || [];
        }
        this.historyLoading = false;
      },
      error: () => {
        this.historyLoading = false;
      }
    });
  }

  rollbackToVersion(version: KAIPromptVersionDto): void {
    if (!this.selectedPrompt) return;

    this.promptService.rollback({
      promptKey: this.selectedPrompt.promptKey,
      versionId: version.versionId
    }).subscribe({
      next: (response) => {
        if (response?.success) {
          this.toastService.show({
            type: 'success',
            data: [this.translateService.instant('KAIPrompts.rolledBack')]
          });
          this.historyModalRef?.close();
          this.loadPrompts();
        }
      }
    });
  }

  closeHistory(): void {
    this.historyModalRef?.close();
  }

  // ═══════════════════════════════════════════
  // Test Prompt
  // ═══════════════════════════════════════════

  openTest(prompt: KAIPromptDto): void {
    this.selectedPrompt = prompt;
    this.testMessage = '';
    this.testResult = '';
    this.testTokens = 0;
    this.testCost = 0;
    this.testing = false;
    this.testModalRef = this.modalService.open(this.testModal, {
      size: 'lg',
      centered: true
    });
  }

  runTest(): void {
    if (!this.selectedPrompt || !this.testMessage.trim()) return;
    this.testing = true;
    this.testResult = '';

    const composed = this.selectedPrompt.constraints
      ? `${this.selectedPrompt.constraints}\n\n---\n\n${this.editValue || this.selectedPrompt.promptValue}`
      : (this.editValue || this.selectedPrompt.promptValue);

    this.promptService.testPrompt({
      systemPrompt: composed,
      testMessage: this.testMessage,
      maxTokens: 2000
    }).subscribe({
      next: (response) => {
        if (response?.success && response.result) {
          this.testResult = response.result.generatedText || '';
          this.testTokens = response.result.tokensUsed;
          this.testCost = response.result.estimatedCost;
        }
        this.testing = false;
      },
      error: () => {
        this.testing = false;
        this.testResult = 'Feil ved testing av prompt.';
      }
    });
  }

  closeTest(): void {
    this.testModalRef?.close();
  }

  // ═══════════════════════════════════════════
  // Workshop
  // ═══════════════════════════════════════════

  openWorkshop(prompt: KAIPromptDto): void {
    this.selectedPrompt = prompt;
    this.workshopOperation = 'validate';
    this.workshopContext = '';
    this.workshopResult = '';
    this.workshopTokens = 0;
    this.workshopLoading = false;
    this.workshopModalRef = this.modalService.open(this.workshopModal, {
      size: 'lg',
      centered: true
    });
  }

  runWorkshop(): void {
    if (!this.selectedPrompt) return;
    this.workshopLoading = true;
    this.workshopResult = '';

    this.promptService.workshop({
      operation: this.workshopOperation,
      currentPrompt: this.editValue || this.selectedPrompt.promptValue,
      context: this.workshopContext || undefined
    }).subscribe({
      next: (response) => {
        if (response?.success && response.result) {
          this.workshopResult = response.result.generatedText || '';
          this.workshopTokens = response.result.tokensUsed;
        }
        this.workshopLoading = false;
      },
      error: () => {
        this.workshopLoading = false;
      }
    });
  }

  applyWorkshopResult(): void {
    if (this.workshopResult) {
      this.editValue = this.workshopResult;
      this.workshopModalRef?.close();
      this.toastService.show({
        type: 'info',
        data: [this.translateService.instant('KAIPrompts.workshopApplied')]
      });
    }
  }

  closeWorkshop(): void {
    this.workshopModalRef?.close();
  }

  // ═══════════════════════════════════════════
  // Reset to Default
  // ═══════════════════════════════════════════

  openResetModal(prompt: KAIPromptDto): void {
    this.promptToReset = prompt;
    this.resetModalRef = this.modalService.open(this.resetConfirmModal, {
      centered: true,
      backdrop: 'static'
    });
  }

  confirmReset(): void {
    if (!this.promptToReset) return;

    this.promptService.resetToDefault(this.promptToReset.promptKey).subscribe({
      next: (response) => {
        if (response?.success) {
          this.toastService.show({
            type: 'success',
            data: [this.translateService.instant('KAIPrompts.resetSuccess')]
          });
          this.resetModalRef?.close();
          this.promptToReset = null;
          this.loadPrompts();
        }
      }
    });
  }

  cancelReset(): void {
    this.resetModalRef?.close();
    this.promptToReset = null;
  }

  // ═══════════════════════════════════════════
  // Helpers
  // ═══════════════════════════════════════════

  getShortKey(key: string): string {
    return key.replace('KAI.Prompt.', '');
  }

  getOutputFormatBadgeClass(format: string): string {
    switch (format) {
      case 'JSON': return 'badge-soft-info';
      case 'Markdown': return 'badge-soft-dark';
      case 'HTML': return 'badge-soft-warning';
      default: return 'badge-soft-success';
    }
  }

  truncate(text: string, maxLength = 200): string {
    if (!text || text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  }
}
